import { IntegrationWithJobInfo } from "@/api/integration"
import { allIntegrations } from "@/setup/connect/integrations"
import { getIntegrationAuthType, getOauthProviderForIntegration, isKnownIntegrationName } from "@/setup/connect/auth_type"
import { capitalCase } from "change-case"
import { Button } from "@/components/ui/button"
import { useLocation } from "wouter-preact"
import { IntegrationSettingsLayout, SettingsSection } from "./layout"
import { SyncStatusDisplay } from "./sync-status-row"
import { AccountingSyncedDataSection } from "../accounting_synced_data"
import { isAccountingIntegrationName } from "@/api/integration"
import { UpdateCredentialsForm } from "./update-credentials-form"
import { ReconnectOauthButton } from "./reconnect-oauth-button"
import { ConnectionInfoSection } from "./connection-info-section"
import { WalmartBotTokenSettings } from "./walmart/bot-token-settings"
import { WalmartUploadSection } from "./walmart-upload-section"
import { WalmartAllowanceSettings } from "./walmart-allowance-settings"
import { TargetUploadSection } from "./target-upload-section"
import { TargetPortalSyncSection } from "@/setup/connect/target/page"
import { DotFoodsUploadSection } from "./dotfoods-upload-section"
import { HyVeeUploadSection } from "./hyvee-upload-section"
import { AlbertsonsUploadSection } from "@/setup/connect/albertsons/upload_section"
import { ContractsList } from "@/contracts/contracts-list"
import { IntegrationCategory } from "@/setup/connect/types"

/** Contract uploads apply to distributors/retailers/logistics (and custom connections), not accounting. */
export function shouldShowContractDocuments(integrationName: string): boolean {
  const config = allIntegrations.find(i => i.id === integrationName)
  if (!config) {
    return true
  }
  const category: IntegrationCategory = config.category
  return category === "distributor" || category === "retailer" || category === "logistics"
}

interface IntegrationSettingsContentProps {
  integration: IntegrationWithJobInfo
  onRefresh: () => void
}

export function IntegrationSettingsContent({ integration, onRefresh }: IntegrationSettingsContentProps) {
  const [, setLocation] = useLocation()
  const integrationName = integration.name
  const settingsPath = isKnownIntegrationName(integrationName)
    ? `/settings/config/${encodeURIComponent(integrationName)}`
    : `/settings/config/id/${integration.id}`

  const config = allIntegrations.find(i => i.id === integrationName)
  const displayName = config?.name ?? capitalCase(integrationName)
  const logo = config?.logo
  const authType = getIntegrationAuthType(integrationName, integration)
  const oauthProvider = getOauthProviderForIntegration(integrationName)
  const isCustomIntegration = !isKnownIntegrationName(integrationName)
  const showContracts = shouldShowContractDocuments(integrationName)

  return (
    <IntegrationSettingsLayout
      logo={logo}
      name={displayName}
      username={integration.username}
      integrationId={integration.id}>
      <SettingsSection title="Sync Status">
        <SyncStatusDisplay lastSyncJob={integration.last_sync_job} integrationName={integrationName} />
      </SettingsSection>

      {isAccountingIntegrationName(integrationName) && (
        <SettingsSection title="Synced data">
          <AccountingSyncedDataSection embedded />
        </SettingsSection>
      )}

      {(integrationName === "sageintacct" || integrationName === "netsuite") && (
        <SettingsSection title="Accounting settings">
          <p class="text-sm text-gray-600">
            Holding account, clearing accounts, early-pay discounts, credit memo location and the sync window now live
            with the rest of the accounting settings on{" "}
            <a href="/settings/accounting" class="underline">
              Settings › Accounting
            </a>
            .
          </p>
        </SettingsSection>
      )}

      {integrationName === "walmart" && (
        <SettingsSection title="Upload Walmart export">
          <WalmartUploadSection integrationId={integration.id} onUploaded={onRefresh} />
        </SettingsSection>
      )}

      {integrationName === "walmart" && (
        <SettingsSection title="Off-invoice allowance">
          <WalmartAllowanceSettings integration={integration} />
        </SettingsSection>
      )}

      {integrationName === "target" && (
        <SettingsSection title="Upload Target payment export">
          <TargetUploadSection integrationId={integration.id} onUploaded={onRefresh} />
        </SettingsSection>
      )}

      {/* The sync's schedule and status are on the `target-portal` row; the section loads it itself.
          Its login is this row's, saved in the Credentials form below. */}
      {integrationName === "target" && (
        <SettingsSection title="Partners Online sync">
          <TargetPortalSyncSection />
        </SettingsSection>
      )}

      {integrationName === "dotfoods" && (
        <SettingsSection title="Upload Dot Foods Invoice Detail report">
          <DotFoodsUploadSection integrationId={integration.id} onUploaded={onRefresh} />
        </SettingsSection>
      )}

      {integrationName === "hyvee" && (
        <SettingsSection title="Upload Hy-Vee exports">
          <HyVeeUploadSection integrationId={integration.id} onUploaded={onRefresh} />
        </SettingsSection>
      )}

      {integrationName === "albertsons" && (
        <SettingsSection title="Upload the Apex Cash Forecast report">
          <AlbertsonsUploadSection integrationId={integration.id} onUploaded={onRefresh} />
        </SettingsSection>
      )}

      {integrationName === "walmart" && (
        <SettingsSection title="Retail Link bot token">
          <WalmartBotTokenSettings integrationId={integration.id} />
        </SettingsSection>
      )}

      {isCustomIntegration && (
        <SettingsSection title="Connection info">
          <ConnectionInfoSection data={integration.data} />
        </SettingsSection>
      )}

      {authType === "password" && (
        <SettingsSection title="Credentials">
          <UpdateCredentialsForm
            integrationName={integrationName}
            integrationId={integration.id}
            initialUsername={integration.username}
            credentialUpdatedAt={integration.credential_updated_at}
            onSuccess={onRefresh}
          />
        </SettingsSection>
      )}

      {authType === "oauth" && oauthProvider && (
        <SettingsSection title="Connection">
          <ReconnectOauthButton
            provider={oauthProvider}
            displayName={displayName}
            returnPath={settingsPath}
            mailbox={integration.username}
            sharedMailbox={integration.data?.mailbox === true}
          />
        </SettingsSection>
      )}

      {authType === "oauth" && !oauthProvider && integrationName === "netsuite" && (
        <SettingsSection title="Connection">
          <p class="text-sm text-gray-500 mb-3">Reconnect your NetSuite account to refresh credentials.</p>
          <Button variant="outline" onClick={() => setLocation("/settings/connect/netsuite")}>
            Reconnect NetSuite
          </Button>
        </SettingsSection>
      )}

      {showContracts && (
        <SettingsSection title="Contract Documents">
          <ContractsList source={integrationName} integrationId={integration.id} />
        </SettingsSection>
      )}
    </IntegrationSettingsLayout>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("shouldShowContractDocuments", () => {
    it("shows for distributors and retailers", () => {
      expect(shouldShowContractDocuments("cands")).toBe(true)
      expect(shouldShowContractDocuments("target")).toBe(true)
      expect(shouldShowContractDocuments("hyvee")).toBe(true)
    })

    it("shows for logistics", () => {
      expect(shouldShowContractDocuments("stord")).toBe(true)
      expect(shouldShowContractDocuments("sps")).toBe(true)
      expect(shouldShowContractDocuments("bol-email-sync")).toBe(true)
    })

    it("hides for accounting", () => {
      expect(shouldShowContractDocuments("xero")).toBe(false)
      expect(shouldShowContractDocuments("quickbooks")).toBe(false)
    })

    it("shows for custom / unknown connection names", () => {
      expect(shouldShowContractDocuments("my-custom-distributor")).toBe(true)
    })
  })
}
